import { useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Textarea } from "@/components/ui/textarea";
import { Input } from "@/components/ui/input";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { ScrollArea } from "@/components/ui/scroll-area";
import { toast } from "@/hooks/use-toast";
import { 
  MessageSquare, 
  CheckCircle, 
  AlertTriangle, 
  Clock, 
  Eye, 
  Trash2, 
  Search,
  Bot,
  User,
  RefreshCw
} from "lucide-react";
import { format } from "date-fns";
import { fr } from "date-fns/locale";

interface Message {
  role: "user" | "assistant";
  content: string;
  timestamp?: string;
  index?: number;
}

interface Conversation {
  id: string;
  session_id: string;
  created_at: string;
  updated_at: string;
  status: "open" | "resolved" | "escalated";
  messages: Message[];
  user_email: string | null;
  user_ip: string | null;
  user_agent: string | null;
  resolved_at: string | null;
  resolved_by: string | null;
  admin_notes: string | null;
  tags: string[];
}

interface AdminChatbotSectionProps {
  emailToUsername?: Record<string, string>;
}

const AdminChatbotSection = ({ emailToUsername = {} }: AdminChatbotSectionProps) => {
  const [selectedConversation, setSelectedConversation] = useState<Conversation | null>(null);
  const [adminNotes, setAdminNotes] = useState("");
  const [searchTerm, setSearchTerm] = useState("");
  const [statusFilter, setStatusFilter] = useState<string>("all");
  const queryClient = useQueryClient();

  // Fetch conversations
  const { data: conversations, isLoading, refetch } = useQuery({
    queryKey: ["chatbot-conversations", statusFilter],
    queryFn: async () => {
      let query = supabase
        .from("chatbot_conversations")
        .select("*")
        .order("created_at", { ascending: false });

      if (statusFilter !== "all") {
        query = query.eq("status", statusFilter);
      }

      const { data, error } = await query;
      if (error) throw error;
      return (data || []).map((conv) => ({
        ...conv,
        messages: (conv.messages as unknown as Message[]) || [],
        status: conv.status as "open" | "resolved" | "escalated",
      })) as Conversation[];
    },
  });

  // Update conversation status
  const updateStatusMutation = useMutation({
    mutationFn: async ({ id, status, notes }: { id: string; status: string; notes?: string }) => {
      const updateData: Record<string, unknown> = { status };
      
      if (status === "resolved") {
        updateData.resolved_at = new Date().toISOString();
      }
      
      if (notes !== undefined) {
        updateData.admin_notes = notes;
      }

      const { error } = await supabase
        .from("chatbot_conversations")
        .update(updateData)
        .eq("id", id);

      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["chatbot-conversations"] });
      toast({ title: "Statut mis à jour" });
    },
    onError: (error) => {
      toast({ title: "Erreur", description: String(error), variant: "destructive" });
    },
  });

  // Delete conversation
  const deleteMutation = useMutation({
    mutationFn: async (id: string) => {
      const { error } = await supabase
        .from("chatbot_conversations")
        .delete()
        .eq("id", id);

      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["chatbot-conversations"] });
      setSelectedConversation(null);
      toast({ title: "Conversation supprimée" });
    },
    onError: (error) => {
      toast({ title: "Erreur", description: String(error), variant: "destructive" });
    },
  });

  // Filter conversations by search
  const filteredConversations = conversations?.filter((conv) => {
    if (!searchTerm) return true;
    const searchLower = searchTerm.toLowerCase();
    const messagesText = conv.messages.map((m) => m.content).join(" ").toLowerCase();
    return (
      messagesText.includes(searchLower) ||
      conv.user_email?.toLowerCase().includes(searchLower) ||
      conv.user_ip?.includes(searchTerm)
    );
  });

  const getStatusBadge = (status: string) => {
    switch (status) {
      case "resolved":
        return <Badge className="bg-green-500/20 text-green-400 border-green-500/30"><CheckCircle className="w-3 h-3 mr-1" />Résolu</Badge>;
      case "escalated":
        return <Badge className="bg-red-500/20 text-red-400 border-red-500/30"><AlertTriangle className="w-3 h-3 mr-1" />Escaladé</Badge>;
      default:
        return <Badge className="bg-yellow-500/20 text-yellow-400 border-yellow-500/30"><Clock className="w-3 h-3 mr-1" />Ouvert</Badge>;
    }
  };

  const getFirstUserMessage = (messages: Message[]) => {
    const userMsg = messages.find((m) => m.role === "user");
    return userMsg?.content.slice(0, 80) + (userMsg?.content && userMsg.content.length > 80 ? "..." : "") || "Pas de message";
  };

  const stats = {
    total: conversations?.length || 0,
    open: conversations?.filter((c) => c.status === "open").length || 0,
    resolved: conversations?.filter((c) => c.status === "resolved").length || 0,
    escalated: conversations?.filter((c) => c.status === "escalated").length || 0,
  };

  return (
    <div className="space-y-6">
      {/* Stats Cards */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <Card className="bg-card border-border">
          <CardContent className="p-4 text-center">
            <MessageSquare className="w-6 h-6 mx-auto mb-2 text-primary" />
            <p className="text-2xl font-bold">{stats.total}</p>
            <p className="text-sm text-muted-foreground">Total</p>
          </CardContent>
        </Card>
        <Card className="bg-card border-border">
          <CardContent className="p-4 text-center">
            <Clock className="w-6 h-6 mx-auto mb-2 text-yellow-500" />
            <p className="text-2xl font-bold">{stats.open}</p>
            <p className="text-sm text-muted-foreground">Ouverts</p>
          </CardContent>
        </Card>
        <Card className="bg-card border-border">
          <CardContent className="p-4 text-center">
            <CheckCircle className="w-6 h-6 mx-auto mb-2 text-green-500" />
            <p className="text-2xl font-bold">{stats.resolved}</p>
            <p className="text-sm text-muted-foreground">Résolus</p>
          </CardContent>
        </Card>
        <Card className="bg-card border-border">
          <CardContent className="p-4 text-center">
            <AlertTriangle className="w-6 h-6 mx-auto mb-2 text-red-500" />
            <p className="text-2xl font-bold">{stats.escalated}</p>
            <p className="text-sm text-muted-foreground">Escaladés</p>
          </CardContent>
        </Card>
      </div>

      {/* Filters */}
      <Card className="bg-card border-border">
        <CardContent className="p-4">
          <div className="flex flex-col md:flex-row gap-4">
            <div className="flex-1 relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
              <Input
                placeholder="Rechercher dans les conversations..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="pl-10"
              />
            </div>
            <div className="flex gap-2">
              <Button
                variant={statusFilter === "all" ? "default" : "outline"}
                size="sm"
                onClick={() => setStatusFilter("all")}
              >
                Tous
              </Button>
              <Button
                variant={statusFilter === "open" ? "default" : "outline"}
                size="sm"
                onClick={() => setStatusFilter("open")}
              >
                Ouverts
              </Button>
              <Button
                variant={statusFilter === "escalated" ? "default" : "outline"}
                size="sm"
                onClick={() => setStatusFilter("escalated")}
              >
                Escaladés
              </Button>
              <Button
                variant={statusFilter === "resolved" ? "default" : "outline"}
                size="sm"
                onClick={() => setStatusFilter("resolved")}
              >
                Résolus
              </Button>
              <Button variant="ghost" size="icon" onClick={() => refetch()}>
                <RefreshCw className="w-4 h-4" />
              </Button>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Conversations List */}
      <Card className="bg-card border-border">
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <MessageSquare className="w-5 h-5" />
            Conversations ({filteredConversations?.length || 0})
          </CardTitle>
        </CardHeader>
        <CardContent>
          {isLoading ? (
            <div className="text-center py-8 text-muted-foreground">Chargement...</div>
          ) : filteredConversations?.length === 0 ? (
            <div className="text-center py-8 text-muted-foreground">Aucune conversation trouvée</div>
          ) : (
            <div className="space-y-3">
              {filteredConversations?.map((conv) => (
                <div
                  key={conv.id}
                  className="flex items-center justify-between p-4 rounded-lg bg-muted/50 hover:bg-muted transition-colors cursor-pointer"
                  onClick={() => {
                    setSelectedConversation(conv);
                    setAdminNotes(conv.admin_notes || "");
                  }}
                >
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 mb-1">
                      {getStatusBadge(conv.status)}
                      <span className="text-xs text-muted-foreground">
                        {format(new Date(conv.created_at), "dd MMM yyyy HH:mm", { locale: fr })}
                      </span>
                      {conv.user_email && (
                        <span className="text-xs text-primary">{conv.user_email}</span>
                      )}
                      {conv.user_email && emailToUsername[conv.user_email.toLowerCase()] && (
                        <Badge variant="outline" className="text-[10px] px-1.5 py-0 border-primary/50 text-primary">
                          👤 {emailToUsername[conv.user_email.toLowerCase()]}
                        </Badge>
                      )}
                    </div>
                    <p className="text-sm truncate">{getFirstUserMessage(conv.messages)}</p>
                    <p className="text-xs text-muted-foreground mt-1">
                      {conv.messages.length} messages
                    </p>
                  </div>
                  <Button variant="ghost" size="icon">
                    <Eye className="w-4 h-4" />
                  </Button>
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>

      {/* Conversation Detail Dialog */}
      <Dialog open={!!selectedConversation} onOpenChange={(open) => !open && setSelectedConversation(null)}>
        <DialogContent className="max-w-2xl max-h-[90vh] flex flex-col">
          <DialogHeader>
            <DialogTitle className="flex items-center justify-between">
              <span>Détail de la conversation</span>
              {selectedConversation && getStatusBadge(selectedConversation.status)}
            </DialogTitle>
          </DialogHeader>

          {selectedConversation && (
            <div className="flex flex-col gap-4 flex-1 min-h-0">
              {/* Metadata */}
              <div className="grid grid-cols-2 gap-2 text-sm">
                <div>
                  <span className="text-muted-foreground">Date: </span>
                  {format(new Date(selectedConversation.created_at), "dd/MM/yyyy HH:mm", { locale: fr })}
                </div>
                <div>
                  <span className="text-muted-foreground">Email: </span>
                  {selectedConversation.user_email || "Non renseigné"}
                </div>
                {selectedConversation.user_email && emailToUsername[selectedConversation.user_email.toLowerCase()] && (
                  <div>
                    <span className="text-muted-foreground">Username: </span>
                    <span className="font-medium text-primary">{emailToUsername[selectedConversation.user_email.toLowerCase()]}</span>
                  </div>
                )}
                <div>
                  <span className="text-muted-foreground">IP: </span>
                  {selectedConversation.user_ip || "Inconnue"}
                </div>
                <div>
                  <span className="text-muted-foreground">Messages: </span>
                  {selectedConversation.messages.length}
                </div>
              </div>

              {/* Messages */}
              <ScrollArea className="flex-1 border rounded-lg p-4 max-h-[300px]">
                <div className="space-y-3">
                  {selectedConversation.messages.map((msg, idx) => (
                    <div
                      key={idx}
                      className={`flex gap-2 ${msg.role === "user" ? "flex-row-reverse" : ""}`}
                    >
                      <div className={`w-6 h-6 rounded-full flex items-center justify-center shrink-0 ${
                        msg.role === "user" ? "bg-primary" : "bg-muted"
                      }`}>
                        {msg.role === "user" ? (
                          <User className="w-3 h-3 text-primary-foreground" />
                        ) : (
                          <Bot className="w-3 h-3" />
                        )}
                      </div>
                      <div
                        className={`rounded-lg px-3 py-2 max-w-[80%] text-sm ${
                          msg.role === "user"
                            ? "bg-primary text-primary-foreground"
                            : "bg-muted"
                        }`}
                      >
                        {msg.content}
                      </div>
                    </div>
                  ))}
                </div>
              </ScrollArea>

              {/* Admin Notes */}
              <div>
                <label className="text-sm font-medium mb-1 block">Notes admin</label>
                <Textarea
                  value={adminNotes}
                  onChange={(e) => setAdminNotes(e.target.value)}
                  placeholder="Ajouter des notes sur cette conversation..."
                  rows={2}
                />
              </div>

              {/* Actions */}
              <div className="flex flex-wrap gap-2">
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => {
                    updateStatusMutation.mutate({
                      id: selectedConversation.id,
                      status: "resolved",
                      notes: adminNotes,
                    });
                    setSelectedConversation(null);
                  }}
                  disabled={selectedConversation.status === "resolved"}
                >
                  <CheckCircle className="w-4 h-4 mr-1" />
                  Marquer résolu
                </Button>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => {
                    updateStatusMutation.mutate({
                      id: selectedConversation.id,
                      status: "escalated",
                      notes: adminNotes,
                    });
                    setSelectedConversation(null);
                  }}
                  disabled={selectedConversation.status === "escalated"}
                >
                  <AlertTriangle className="w-4 h-4 mr-1" />
                  Escalader
                </Button>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => {
                    updateStatusMutation.mutate({
                      id: selectedConversation.id,
                      status: "open",
                      notes: adminNotes,
                    });
                    setSelectedConversation(null);
                  }}
                  disabled={selectedConversation.status === "open"}
                >
                  <Clock className="w-4 h-4 mr-1" />
                  Réouvrir
                </Button>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => {
                    updateStatusMutation.mutate({
                      id: selectedConversation.id,
                      status: selectedConversation.status,
                      notes: adminNotes,
                    });
                  }}
                >
                  Sauvegarder notes
                </Button>
                <Button
                  variant="destructive"
                  size="sm"
                  onClick={() => {
                    if (confirm("Supprimer cette conversation ?")) {
                      deleteMutation.mutate(selectedConversation.id);
                    }
                  }}
                >
                  <Trash2 className="w-4 h-4 mr-1" />
                  Supprimer
                </Button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default AdminChatbotSection;
